import React, { FC, useEffect, useMemo, useState } from "react";
import MaterialReactTable, { MRT_ColumnDef } from "material-react-table";
import type {
  ColumnFiltersState,
  PaginationState,
  SortingState,
} from "@tanstack/react-table";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

type LessonApiResponse = {
  lessons: Array<Lesson>;
  total: number;
};

type Lesson = {
  classID: string;
  firstLesson: Date;
  courseID: string;
  subCateg1: string;
  subCateg2: string;
  title: string;
};

const CONSTS = {
  PERIOD: 16,
};

const App: FC = () => {
  const [data, setData] = useState<Lesson[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 100,
  });
  const [rowCount, setRowCount] = useState(0);
  dayjs.extend(customParseFormat);

  useEffect(() => {
    const fetchData = async () => {
      if (!data.length) {
        setIsLoading(true);
      } else {
        setIsRefetching(true);
      }

      const url = new URL(
        "/site/fetchLessonsForPeriod",
        // "/programme",
        process.env.NODE_ENV === "development"
          ? "https://vitali-backend.ey.r.appspot.com"
          : "https://vitali-backend.ey.r.appspot.com"
      );
      // https://vitali-backend.ey.r.appspot.com
      // https://webapp.vitali.gr/site/fetchLessonsForPeriod?period=12&page=1&recsPerPage=200
      url.searchParams.set("period", `${CONSTS.PERIOD}`);
      url.searchParams.set("page", `${pagination.pageIndex}`);
      url.searchParams.set("recsPerPage", `${pagination.pageSize}`);
      // url.searchParams.set("globalFilter", globalFilter ?? "");

      try {
        const response = await fetch(url.href, {
          method: "GET",
          mode: "cors",
        });
        const json = (await response.json()) as LessonApiResponse;

        setData(json.lessons);
        setRowCount(json.total);
      } catch (error) {
        setIsError(true);
        console.error(error);
        return;
      }
      setIsError(false);
      setIsLoading(false);
      setIsRefetching(false);
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    columnFilters,
    globalFilter,
    pagination.pageIndex,
    pagination.pageSize,
    sorting,
  ]);

  const columns = useMemo<MRT_ColumnDef<Lesson>[]>(
    () => [
      {
        accessorKey: "subCateg1",
        header: "Πανεπιστήμιο",
        enableColumnActions: false,
      },
      {
        accessorKey: "subCateg2",
        header: "Σχολή/Τμήμα",
        enableColumnActions: false,
      },
      {
        accessorKey: "title",
        header: "Mάθημα",
        enableColumnActions: false,
      },
      {
        accessorKey: "firstLesson",
        header: "Ημερομηνία/'Ωρα Έναρξης",
        Cell: ({ cell }) =>
          dayjs(cell.getValue<string>()).format("DD/MM/YYYY - HH:mm"),

        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
        },
        enableSorting: false,
        enableColumnActions: false,
      },
    ],
    []
  );

  return (
    <MaterialReactTable
      columns={columns}
      data={data}
      enableRowSelection={false}
      // getRowId={(row) => row.classID}
      initialState={{ showColumnFilters: false, density: "compact" }}
      enableClickToCopy={true}
      manualFiltering={false}
      manualPagination
      enableDensityToggle={false}
      enableColumnFilters={false}
      enableFullScreenToggle={false}
      enableHiding={false}
      muiToolbarAlertBannerProps={
        isError
          ? {
              color: "error",
              children: "Error loading data",
            }
          : undefined
      }
      onGlobalFilterChange={setGlobalFilter}
      onPaginationChange={setPagination}
      onSortingChange={setSorting}
      rowCount={rowCount}
      state={{
        // columnFilters,
        globalFilter,
        isLoading,
        pagination,
        showAlertBanner: isError,
        showProgressBars: isRefetching,
        sorting,
        // showGlobalFilter: true,
      }}
    />
  );
};

export default App;
